import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Organization Data Module Service Stores
 */
export const organizationDataStores: ServiceStoreConfig[] = [
  {
    name: "project-phases",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "phaseId",
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectPhasesStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalPhases = entities.length;
          const activePhases = entities.filter(
            (phase: Record<string, unknown>) =>
              phase.status === "active" || phase.isActive !== false
          ).length;
          const completedPhases = entities.filter(
            (phase: Record<string, unknown>) =>
              phase.status === "completed" || phase.isCompleted
          ).length;
          const inProgressPhases = entities.filter(
            (phase: Record<string, unknown>) =>
              phase.status === "in_progress" || phase.status === "ongoing"
          ).length;

          return [
            {
              title: "Total Phases",
              value: () => totalPhases.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activePhases.toString(),
              valueColor: "success.main",
            },
            {
              title: "In Progress",
              value: () => inProgressPhases.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Completed",
              value: () => completedPhases.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "project-phase-items",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "phaseItemId",
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectPhaseItemsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalItems = entities.length;
          const completedItems = entities.filter(
            (item: Record<string, unknown>) =>
              item.status === "completed" || item.isCompleted
          ).length;
          const inProgressItems = entities.filter(
            (item: Record<string, unknown>) =>
              item.status === "in_progress" || item.status === "ongoing"
          ).length;
          const pendingItems = entities.filter(
            (item: Record<string, unknown>) =>
              item.status === "pending" || item.status === "not_started"
          ).length;

          return [
            {
              title: "Total Items",
              value: () => totalItems.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Completed",
              value: () => completedItems.toString(),
              valueColor: "success.main",
            },
            {
              title: "In Progress",
              value: () => inProgressItems.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Pending",
              value: () => pendingItems.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "projects",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalProjects = entities.length;
          const activeProjects = entities.filter(
            (project: Record<string, unknown>) =>
              project.status === "active" ||
              project.status === "in_progress" ||
              project.isDeleted !== true
          ).length;
          const completedProjects = entities.filter(
            (project: Record<string, unknown>) => project.status === "completed"
          ).length;
          const totalValue = (entities as Record<string, unknown>[]).reduce(
            (sum: number, project) => {
              return sum + ((project.totalValue as number) || 0);
            },
            0
          );

          const formatCurrency = (value: number) =>
            new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);

          return [
            {
              title: "Total Projects",
              value: () => totalProjects.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeProjects.toString(),
              valueColor: "success.main",
            },
            {
              title: "Completed",
              value: () => completedProjects.toString(),
              valueColor: "info.main",
            },
            {
              title: "Total Value",
              value: () => formatCurrency(totalValue),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "project-activity",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectActivityStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalActivities = entities.length;
          const createActivities = entities.filter(
            (activity: Record<string, unknown>) =>
              activity.activityType === "create" ||
              activity.activityType === "created"
          ).length;
          const updateActivities = entities.filter(
            (activity: Record<string, unknown>) =>
              activity.activityType === "update" ||
              activity.activityType === "updated"
          ).length;
          const commentActivities = entities.filter(
            (activity: Record<string, unknown>) =>
              activity.activityType === "comment" ||
              activity.activityType === "note"
          ).length;

          return [
            {
              title: "Total Activities",
              value: () => totalActivities.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Creates",
              value: () => createActivities.toString(),
              valueColor: "success.main",
            },
            {
              title: "Updates",
              value: () => updateActivities.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Comments",
              value: () => commentActivities.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "project-documents",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectDocumentsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalDocuments = entities.length;
          const activeDocuments = entities.filter(
            (doc: Record<string, unknown>) => doc.isActive === true
          ).length;
          const archivedDocuments = entities.filter(
            (doc: Record<string, unknown>) => doc.isActive === false
          ).length;
          const totalSizeBytes = (entities as Record<string, unknown>[]).reduce(
            (sum: number, doc) => {
              return sum + ((doc.fileSize as number) || 0);
            },
            0
          );
          const totalSizeMB = (totalSizeBytes / (1024 * 1024)).toFixed(1);

          return [
            {
              title: "Total Documents",
              value: () => totalDocuments.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeDocuments.toString(),
              valueColor: "success.main",
            },
            {
              title: "Archived",
              value: () => archivedDocuments.toString(),
              valueColor: "info.main",
            },
            {
              title: "Total Size",
              value: () => `${totalSizeMB} MB`,
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "project-financials",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectFinancialsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalRecords = entities.length;
          const totalRevenue = (entities as Record<string, unknown>[]).reduce(
            (sum: number, fin) => {
              return sum + ((fin.actualRevenue as number) || 0);
            },
            0
          );
          const totalCost = (entities as Record<string, unknown>[]).reduce(
            (sum: number, fin) => {
              return sum + ((fin.actualCost as number) || 0);
            },
            0
          );
          const totalMargin = totalRevenue - totalCost;

          const formatCurrency = (value: number) =>
            new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);

          return [
            {
              title: "Records",
              value: () => totalRecords.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Revenue",
              value: () => formatCurrency(totalRevenue),
              valueColor: "success.main",
            },
            {
              title: "Cost",
              value: () => formatCurrency(totalCost),
              valueColor: "warning.main",
            },
            {
              title: "Margin",
              value: () => formatCurrency(totalMargin),
              valueColor: totalMargin >= 0 ? "success.main" : "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "project-milestones",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectMilestonesStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalMilestones = entities.length;
          const completedMilestones = entities.filter(
            (milestone: Record<string, unknown>) =>
              milestone.status === "completed"
          ).length;
          const pendingMilestones = entities.filter(
            (milestone: Record<string, unknown>) =>
              milestone.status === "pending" ||
              milestone.status === "not_started"
          ).length;
          const now = new Date();
          const overdueMilestones = entities.filter(
            (milestone: Record<string, unknown>) => {
              if (milestone.status === "completed") return false;
              const dueDate = milestone.dueDate
                ? new Date(milestone.dueDate as string)
                : null;
              return dueDate && dueDate < now;
            }
          ).length;

          return [
            {
              title: "Total Milestones",
              value: () => totalMilestones.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Completed",
              value: () => completedMilestones.toString(),
              valueColor: "success.main",
            },
            {
              title: "Pending",
              value: () => pendingMilestones.toString(),
              valueColor: "info.main",
            },
            {
              title: "Overdue",
              value: () => overdueMilestones.toString(),
              valueColor: overdueMilestones > 0 ? "error.main" : "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "plants",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.plantsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalPlants = entities.length;
          const activePlants = entities.filter(
            (plant: Record<string, unknown>) => plant.isActive === true
          ).length;
          const inactivePlants = entities.filter(
            (plant: Record<string, unknown>) => plant.isActive === false
          ).length;
          const statesRepresented = new Set(
            (entities as Record<string, unknown>[])
              .map((plant) => plant.state)
              .filter(Boolean)
          ).size;

          return [
            {
              title: "Total Plants",
              value: () => totalPlants.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activePlants.toString(),
              valueColor: "success.main",
            },
            {
              title: "Inactive",
              value: () => inactivePlants.toString(),
              valueColor: "warning.main",
            },
            {
              title: "States",
              value: () => statesRepresented.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "companies",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.companiesStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalCompanies = entities.length;
          const activeCompanies = entities.filter(
            (company: Record<string, unknown>) => company.isActive === true
          ).length;
          const inactiveCompanies = entities.filter(
            (company: Record<string, unknown>) => company.isActive === false
          ).length;

          return [
            {
              title: "Total Companies",
              value: () => totalCompanies.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeCompanies.toString(),
              valueColor: "success.main",
            },
            {
              title: "Inactive",
              value: () => inactiveCompanies.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];
